import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { tasksAPI, usersAPI, studentsAPI, restaurantsAPI, placementsAPI } from '../lib/api';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', dot: 'bg-cyan-500' },
  { value: 'medium', label: 'Medium', dot: 'bg-amber-500' },
  { value: 'high', label: 'High', dot: 'bg-red-500' },
];

/**
 * Reusable Task Create/Edit modal.
 * Props:
 *  - open, onOpenChange
 *  - task (object | null) — when set, edit mode
 *  - defaultDate (YYYY-MM-DD) — pre-fill due_date for new tasks
 *  - onSaved(task)
 *  - onDeleted(taskId)
 */
const TaskModal = ({ open, onOpenChange, task, defaultDate, onSaved, onDeleted }) => {
  const isEdit = !!task;
  const [form, setForm] = useState(() => emptyForm(defaultDate));
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Lookups
    Promise.all([
      usersAPI.getAll().catch(() => ({ data: [] })),
      studentsAPI.getAll().catch(() => ({ data: [] })),
      restaurantsAPI.getAll().catch(() => ({ data: [] })),
      placementsAPI.getAll().catch(() => ({ data: [] })),
    ]).then(([uRes, sRes, rRes, pRes]) => {
      setUsers(uRes.data || []);
      setStudents(sRes.data || []);
      setRestaurants(rRes.data || []);
      setPlacements(pRes.data || []);
    });
    // Form state
    if (task) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date || defaultDate || todayIso(),
        priority: task.priority || 'medium',
        status: task.status || 'pending',
        assignee_user_id: task.assignee_user_id || '',
        related_type: task.related_type || '',
        related_id: task.related_id || '',
      });
    } else {
      setForm(emptyForm(defaultDate));
    }
  }, [open, task, defaultDate]);

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || '',
        due_date: form.due_date,
        priority: form.priority,
        status: form.status,
        assignee_user_id: form.assignee_user_id || null,
        related_type: form.related_type || null,
        related_id: form.related_id || null,
      };
      const res = isEdit
        ? await tasksAPI.update(task.id, payload)
        : await tasksAPI.create(payload);
      toast.success(isEdit ? 'Task updated' : 'Task created');
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      await tasksAPI.delete(task.id);
      toast.success('Task deleted');
      onDeleted?.(task.id);
      onOpenChange(false);
    } catch {
      toast.error('Failed to delete task');
    }
  };

  // Options for the linked-entity picker
  const relatedOptions = (() => {
    if (form.related_type === 'student') {
      return students.map((s) => ({ id: s.id, label: `${s.name} (${s.student_number || '-'})` }));
    }
    if (form.related_type === 'restaurant') {
      return restaurants.map((r) => ({ id: r.id, label: r.name }));
    }
    if (form.related_type === 'placement') {
      return placements.map((p) => ({ id: p.id, label: `${p.student_name} @ ${p.restaurant_name}` }));
    }
    return [];
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-modal" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-ica-navy">
            {isEdit ? 'Edit Task' : 'New Task'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update task details below.' : 'Create a new task for action or follow-up.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Title *</Label>
            <Input
              data-testid="task-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Follow up with Spur about Gordon's assessment"
              autoFocus
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              data-testid="task-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional notes…"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Due Date *</Label>
              <Input
                data-testid="task-due-date"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${p.dot}`} />
                        {p.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign To</Label>
              <Select
                value={form.assignee_user_id || '__self__'}
                onValueChange={(v) => setForm({ ...form, assignee_user_id: v === '__self__' ? '' : v })}
              >
                <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Me" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__self__">— Me (default) —</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Link to (optional)</Label>
              <Select
                value={form.related_type || '__none__'}
                onValueChange={(v) => setForm({ ...form, related_type: v === '__none__' ? '' : v, related_id: '' })}
              >
                <SelectTrigger data-testid="task-related-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="restaurant">Restaurant</SelectItem>
                  <SelectItem value="placement">Placement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>&nbsp;</Label>
              <Select
                value={form.related_id || '__pick__'}
                onValueChange={(v) => setForm({ ...form, related_id: v === '__pick__' ? '' : v })}
                disabled={!form.related_type}
              >
                <SelectTrigger data-testid="task-related-id"><SelectValue placeholder="Pick one…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__pick__">— Pick one —</SelectItem>
                  {relatedOptions.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter className="flex sm:justify-between items-center">
          <div>
            {isEdit && (
              <Button
                data-testid="task-delete-btn"
                variant="ghost"
                onClick={handleDelete}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              data-testid="task-save-btn"
              onClick={handleSave}
              disabled={saving}
              
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const emptyForm = (defaultDate) => ({
  title: '',
  description: '',
  due_date: defaultDate || todayIso(),
  priority: 'medium',
  status: 'pending',
  assignee_user_id: '',
  related_type: '',
  related_id: '',
});

const todayIso = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default TaskModal;
